import axios from 'axios'
import { cognitoRefreshSession } from '@/lib/cognito'
import { useAuthStore } from '@/store/authStore'

// ─────────────────────────────────────────────────────────────
// Axios instance pointing to API Gateway
// ─────────────────────────────────────────────────────────────

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL as string,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
})

// ── Request interceptor — attach Bearer token ──────────────────
apiClient.interceptors.request.use((config) => {
  const user = useAuthStore.getState().user
  // API Gateway JWT authorizer richiede l'ID token (ha il campo 'aud')
  // l'access token Cognito non ha 'aud' e verrebbe rifiutato con 401
  if (user?.idToken) {
    config.headers.Authorization = `Bearer ${user.idToken}`
  }
  return config
})

// ── Response interceptor — auto-refresh on 401 ────────────────
let isRefreshing = false
let pendingQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

function processQueue(err: unknown, token: string | null) {
  pendingQueue.forEach(({ resolve, reject }) =>
    err ? reject(err) : resolve(token!)
  )
  pendingQueue = []
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalReq = error.config as typeof error.config & { _retry?: boolean }

    if (error.response?.status === 401 && !originalReq._retry) {
      if (isRefreshing) {
        // Queue request while refresh is in flight
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject })
        }).then((token) => {
          originalReq.headers.Authorization = `Bearer ${token}`
          return apiClient(originalReq)
        })
      }

      originalReq._retry = true
      isRefreshing = true

      try {
        const refreshed = await cognitoRefreshSession()
        if (!refreshed) throw new Error('refresh_failed')

        useAuthStore.getState().setUser(refreshed)
        processQueue(null, refreshed.idToken)

        originalReq.headers.Authorization = `Bearer ${refreshed.idToken}`
        return apiClient(originalReq)
      } catch (err) {
        processQueue(err, null)
        useAuthStore.getState().clearAuth()
        window.location.href = '/login'
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)
